import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject, filter, map, zip } from "rxjs";
import { Comment } from "@webapp/comments";
import { KpiProjection } from "@webapp/kpis/models/kpi-projections.models";
import { KpiSnapshot } from "@webapp/kpis/models/kpi-snapshot.models";
import { Kpi } from "@webapp/kpis/models/kpis.models";
import { KpiCommentFormModalData } from "../../kpi-comments/kpi-comment-form/kpi-comment-form.component";
import { KpiDetailsStateService } from "./kpi-details-state.service";

@Injectable()
export class KpiDetailsStateProxyService {
  private openComment$: BehaviorSubject<Comment> = new BehaviorSubject<Comment>(null);
  private projectionDeleted$: Subject<string> = new Subject<string>();
  private projectionUpdated$: Subject<KpiProjection> = new Subject<KpiProjection>();
  private projectionDragged$: Subject<KpiProjection> = new Subject<KpiProjection>();

  constructor(private kpiDetailsStateService: KpiDetailsStateService) {}

  public getCommentFormData$(): Observable<KpiCommentFormModalData> {
    return zip(
      this.kpiDetailsStateService.getKpi$(),
      this.kpiDetailsStateService.getSnapshots$(),
      this.kpiDetailsStateService.getProjections$(),
      this.kpiDetailsStateService.getComments$()
    ).pipe(
      map(([kpi, snapshots, projections, comments]) => ({
        kpi,
        snapshots,
        projections,
        comments: comments.filter((comment) => comment.targetType !== "kpi"),
        onCommentCreated: this.onCommentCreated.bind(this),
      }))
    );
  }

  public getComment$(id: string): Observable<Comment> {
    return this.kpiDetailsStateService.getComments$().pipe(
      map((comments) => comments.find((comment) => comment.id === id)),
      filter((comment) => !!comment)
    );
  }

  public getProjectionByDate$(projectionDate: string): Observable<KpiProjection> {
    return this.kpiDetailsStateService.getProjections$().pipe(map((projections) => projections.find((projection) => projection.date === projectionDate)));
  }

  public getSnapshotByKey$(snapshotKey: string): Observable<KpiSnapshot> {
    return this.kpiDetailsStateService.getSnapshots$().pipe(map((snapshots) => snapshots.find((snapshot) => snapshot.key === snapshotKey)));
  }

  public getKpi$(): Observable<Kpi> {
    return this.kpiDetailsStateService.getKpi$();
  }

  public onCommentCreated(kpiId: string): void {
    this.kpiDetailsStateService.onCommentCreated(kpiId);
  }

  public onCommentDeleted(commendId: string): void {
    this.kpiDetailsStateService.onCommentDeleted(commendId);
  }

  public setOpenComment(comment: Comment): void {
    this.openComment$.next(comment);
  }

  public getOpenComment$(): Observable<Comment> {
    return this.openComment$.asObservable();
  }

  public getProjectionDeleted$(): Observable<string> {
    return this.projectionDeleted$.asObservable();
  }

  public onProjectionDeleted(projectionDate: string): void {
    this.projectionDeleted$.next(projectionDate);
  }

  public getProjectionUpdated$(): Observable<KpiProjection> {
    return this.projectionUpdated$.asObservable();
  }

  public onProjectionUpdated(projection: KpiProjection): void {
    this.projectionUpdated$.next(projection);
  }

  public getProjectionDragged$(): Observable<KpiProjection> {
    return this.projectionDragged$.asObservable();
  }

  public onProjectionDragged(projection: KpiProjection): void {
    this.projectionDragged$.next(projection);
  }
}
